import type {
  HWPXDocument,
  CharProperty,
  ParaProperty,
  Paragraph,
  TextRun,
  TableRun,
  Table,
  TableRow,
  TableCell,
  Picture,
  PictureRun,
  BinDataItem,
  FontRef,
  Section,
  BorderFill,
  BorderLine,
  BorderLineType,
} from './types.js';
import {
  createDefaultDocument,
  createDefaultCharProperty,
  createDefaultParaProperty,
  createDefaultSectionDef,
  createDefaultBorderFill,
} from './defaults.js';
import { ptToCharHeight, mmToHwpunit } from './utils/units.js';
import { hexToColorref } from './utils/color.js';
import { IdCounter } from './utils/id.js';

export interface TextStyleOptions {
  fontSize?: number;   // pt
  bold?: boolean;
  italic?: boolean;
  color?: string;      // hex (#RRGGBB)
  underline?: boolean;
  strikeout?: boolean;
  fontName?: string;
}

export interface StyledSegment {
  text: string;
  style?: TextStyleOptions;
}

export interface PageSettings {
  width?: number;     // mm
  height?: number;    // mm
  landscape?: boolean;
  marginLeft?: number;    // mm
  marginRight?: number;   // mm
  marginTop?: number;     // mm
  marginBottom?: number;  // mm
}

export interface TableOptions {
  headerRow?: boolean;
  borderStyle?: BorderLineType;
  colWidths?: number[];   // mm 단위 (미지정시 균등 분배)
  cellPadding?: number;   // mm
  width?: number;         // mm (미지정시 본문 폭)
}

export interface ImageOptions {
  width: number;    // mm
  height: number;   // mm
}

export interface CellMerge {
  row: number;
  col: number;
  rowSpan: number;
  colSpan: number;
}

export interface ColumnSettings {
  count: number;
  gap?: number;     // mm (기본 10mm)
  type?: 'NORMAL' | 'DISTRIBUTE' | 'PARALLEL';
}

export interface HeaderFooterOptions {
  header?: string;
  footer?: string;
}

export class HWPXBuilder {
  private doc: HWPXDocument;
  private charPrIdCounter: IdCounter;
  private paraPrIdCounter: IdCounter;
  private borderFillIdCounter: IdCounter;
  private binDataIdCounter: IdCounter;
  private charPrCache = new Map<string, number>();

  constructor() {
    this.doc = createDefaultDocument();
    // 기본 charPr(id=0), paraPr(id=0)가 이미 있으므로 1부터 시작
    this.charPrIdCounter = new IdCounter(1);
    this.paraPrIdCounter = new IdCounter(1);
    this.borderFillIdCounter = new IdCounter(2); // 기본 borderFill(id=1) 이미 있음
    this.binDataIdCounter = new IdCounter(1);
  }

  /** 단일 스타일 문단 추가 */
  addParagraph(text: string, style?: TextStyleOptions): this {
    const charPrId = this.getOrCreateCharPr(style);
    const para: Paragraph = {
      paraPrIDRef: 0,
      styleIDRef: 0,
      runs: [
        {
          t: 'text',
          text,
          charPrIDRef: charPrId,
        },
      ],
    };
    this.currentSection().paragraphs.push(para);
    return this;
  }

  /** 복수 스타일 세그먼트로 구성된 문단 추가 */
  addStyledText(segments: StyledSegment[]): this {
    const runs: TextRun[] = segments.map((seg) => ({
      t: 'text' as const,
      text: seg.text,
      charPrIDRef: this.getOrCreateCharPr(seg.style),
    }));

    const para: Paragraph = {
      paraPrIDRef: 0,
      styleIDRef: 0,
      runs,
    };
    this.currentSection().paragraphs.push(para);
    return this;
  }

  /** 빈 문단(빈 줄) 추가 */
  addEmptyParagraph(): this {
    const para: Paragraph = {
      paraPrIDRef: 0,
      styleIDRef: 0,
      runs: [
        {
          t: 'text',
          text: '',
          charPrIDRef: 0,
        },
      ],
    };
    this.currentSection().paragraphs.push(para);
    return this;
  }

  /** 페이지 설정 변경 */
  setPageSettings(opts: PageSettings): this {
    const def = this.currentSection().def;
    if (opts.width !== undefined) def.pageWidth = mmToHwpunit(opts.width);
    if (opts.height !== undefined) def.pageHeight = mmToHwpunit(opts.height);
    if (opts.landscape !== undefined) def.landscape = opts.landscape;
    if (opts.marginLeft !== undefined) def.pageMargin.left = mmToHwpunit(opts.marginLeft);
    if (opts.marginRight !== undefined) def.pageMargin.right = mmToHwpunit(opts.marginRight);
    if (opts.marginTop !== undefined) def.pageMargin.top = mmToHwpunit(opts.marginTop);
    if (opts.marginBottom !== undefined) def.pageMargin.bottom = mmToHwpunit(opts.marginBottom);
    return this;
  }

  /** 테이블 추가 (string[][] 데이터, 셀 병합은 merges 옵션) */
  addTable(data: string[][], opts?: TableOptions & { merges?: CellMerge[] }): this {
    const rowCount = data.length;
    const colCount = data.length > 0 ? data[0].length : 0;
    if (rowCount === 0 || colCount === 0) return this;

    const secDef = this.currentSection().def;
    const bodyWidth = opts?.width
      ? mmToHwpunit(opts.width)
      : secDef.pageWidth - secDef.pageMargin.left - secDef.pageMargin.right;

    const colWidths = opts?.colWidths
      ? opts.colWidths.map((w) => mmToHwpunit(w))
      : Array(colCount).fill(Math.floor(bodyWidth / colCount));

    const borderFillId = this.getOrCreateTableBorderFill(opts?.borderStyle || 'SOLID');

    const cellPadding = opts?.cellPadding
      ? mmToHwpunit(opts.cellPadding)
      : mmToHwpunit(1.5);

    const rowHeight = mmToHwpunit(10);

    // 병합 맵 구축: mergedCells[row][col] = { rowSpan, colSpan } or 'hidden'
    const mergeMap = new Map<string, { rowSpan: number; colSpan: number } | 'hidden'>();
    if (opts?.merges) {
      for (const m of opts.merges) {
        mergeMap.set(`${m.row},${m.col}`, { rowSpan: m.rowSpan, colSpan: m.colSpan });
        for (let r = m.row; r < m.row + m.rowSpan; r++) {
          for (let c = m.col; c < m.col + m.colSpan; c++) {
            if (r !== m.row || c !== m.col) {
              mergeMap.set(`${r},${c}`, 'hidden');
            }
          }
        }
      }
    }

    const rows: TableRow[] = data.map((rowData, ri) => ({
      height: rowHeight,
      cells: rowData
        .map((cellText, ci) => {
          const key = `${ri},${ci}`;
          const mergeInfo = mergeMap.get(key);
          if (mergeInfo === 'hidden') return null; // 병합으로 숨겨진 셀

          const span = mergeInfo
            ? mergeInfo
            : { rowSpan: 1, colSpan: 1 };

          let cellWidth = 0;
          for (let c = ci; c < ci + span.colSpan && c < colWidths.length; c++) {
            cellWidth += colWidths[c];
          }

          return {
            paragraphs: [{
              paraPrIDRef: 0,
              styleIDRef: 0,
              runs: [{ t: 'text' as const, text: cellText, charPrIDRef: 0 }],
            }],
            colSpan: span.colSpan,
            rowSpan: span.rowSpan,
            width: cellWidth || colWidths[ci] || colWidths[0],
            height: rowHeight * span.rowSpan,
            borderFillIDRef: borderFillId,
            padding: {
              left: cellPadding,
              right: cellPadding,
              top: cellPadding,
              bottom: cellPadding,
            },
          } as TableCell;
        })
        .filter((c): c is TableCell => c !== null),
    }));

    const table: Table = {
      rows,
      borderFillIDRef: borderFillId,
      width: bodyWidth,
      rowCount,
      colCount,
      colWidths,
    };

    const para: Paragraph = {
      paraPrIDRef: 0,
      styleIDRef: 0,
      runs: [{ t: 'table', table, charPrIDRef: 0 }],
    };
    this.currentSection().paragraphs.push(para);
    return this;
  }

  /** 이미지 추가 */
  addImage(data: Uint8Array, format: BinDataItem['format'], opts: ImageOptions): this {
    const id = this.binDataIdCounter.next();
    const name = `image${id}.${format}`;
    this.doc.binData.push({ id, format, name, data });

    const pic: Picture = {
      binDataIDRef: id,
      width: mmToHwpunit(opts.width),
      height: mmToHwpunit(opts.height),
    };

    const para: Paragraph = {
      paraPrIDRef: 0,
      styleIDRef: 0,
      runs: [{ t: 'picture', picture: pic, charPrIDRef: 0 }],
    };
    this.currentSection().paragraphs.push(para);
    return this;
  }

  /** 머리글/바닥글 설정 */
  setHeaderFooter(opts: HeaderFooterOptions): this {
    const def = this.currentSection().def;
    if (!def.headerFooter) def.headerFooter = {};

    if (opts.header !== undefined) {
      def.headerFooter.header = {
        paragraphs: [{
          paraPrIDRef: 0,
          styleIDRef: 0,
          runs: [{ t: 'text' as const, text: opts.header, charPrIDRef: 0 }],
        }],
      };
    }

    if (opts.footer !== undefined) {
      def.headerFooter.footer = {
        paragraphs: [{
          paraPrIDRef: 0,
          styleIDRef: 0,
          runs: [{ t: 'text' as const, text: opts.footer, charPrIDRef: 0 }],
        }],
      };
    }

    return this;
  }

  /** 다단 설정 */
  setColumns(opts: ColumnSettings): this {
    const def = this.currentSection().def;
    def.columns = {
      type: opts.type || 'NORMAL',
      count: opts.count,
      gap: mmToHwpunit(opts.gap ?? 10),
      sameSizes: true,
    };
    return this;
  }

  /** 새 섹션 시작 */
  addSection(): this {
    this.doc.sections.push({
      def: createDefaultSectionDef(),
      paragraphs: [],
    });
    return this;
  }

  /** 문서 빌드 */
  build(): HWPXDocument {
    return this.doc;
  }

  // ─── Internal ───

  private currentSection(): Section {
    return this.doc.sections[this.doc.sections.length - 1];
  }

  private getOrCreateCharPr(style?: TextStyleOptions): number {
    if (!style) return 0; // 기본 charPr

    const key = this.styleKey(style);
    const cached = this.charPrCache.get(key);
    if (cached !== undefined) return cached;

    const id = this.charPrIdCounter.next();
    const base = createDefaultCharProperty(id);

    if (style.fontSize !== undefined) base.height = ptToCharHeight(style.fontSize);
    if (style.bold !== undefined) base.bold = style.bold;
    if (style.italic !== undefined) base.italic = style.italic;
    if (style.color !== undefined) base.textColor = hexToColorref(style.color);
    if (style.underline) base.underline = 'BOTTOM';
    if (style.strikeout) base.strikeout = 'CONTINUOUS';

    if (style.fontName) {
      const fontIdx = this.ensureFont(style.fontName);
      base.fontRef = {
        hangul: fontIdx,
        latin: fontIdx,
        hanja: fontIdx,
        japanese: fontIdx,
        other: fontIdx,
        symbol: fontIdx,
        user: fontIdx,
      };
    }

    this.doc.head.charProperties.push(base);
    this.charPrCache.set(key, id);
    return id;
  }

  private ensureFont(fontName: string): number {
    const faces = this.doc.head.fontFaces;
    // 같은 fontName이 이미 있는지 확인 (HANGUL 기준)
    const hangulFaces = faces.filter((f) => f.lang === 'HANGUL');
    const existing = hangulFaces.findIndex((f) => f.fontName === fontName);
    if (existing !== -1) return existing;

    // 새 폰트 추가 — 모든 lang에 추가
    const newIdx = hangulFaces.length;
    const langs: Array<FontRef[keyof FontRef] extends number ? never : string> = [];
    const allLangs = ['HANGUL', 'LATIN', 'HANJA', 'JAPANESE', 'OTHER', 'SYMBOL', 'USER'] as const;
    for (const lang of allLangs) {
      faces.push({ lang, fontName, fontType: 'ttf' });
    }
    return newIdx;
  }

  private getOrCreateTableBorderFill(lineType: BorderLineType): number {
    const line: BorderLine = {
      type: lineType,
      width: '0.12mm',
      color: hexToColorref('#000000'),
    };
    const bf: BorderFill = {
      id: this.borderFillIdCounter.next(),
      borders: {
        left: { ...line },
        right: { ...line },
        top: { ...line },
        bottom: { ...line },
      },
    };
    this.doc.head.borderFills.push(bf);
    return bf.id;
  }

  private styleKey(style: TextStyleOptions): string {
    return JSON.stringify({
      fs: style.fontSize,
      b: style.bold,
      i: style.italic,
      c: style.color,
      u: style.underline,
      s: style.strikeout,
      f: style.fontName,
    });
  }
}
